import { logger } from '@/lib/logger';
import { supabase } from '@/lib/supabase';

export type SubscriptionInterval = 'month' | 'year' | null;

export interface SubscriptionSaleStatus {
  /** "month" => monthly member, "year" => annual member, null => none / unknown. */
  interval: SubscriptionInterval;
  /**
   * True when the user owns Lifetime Access. Its WC payment plan masquerades
   * as an active monthly subscription, so the edge function detects it by
   * product and reports it separately — lifetime owners get no sale banner.
   */
  hasLifetime: boolean;
  /**
   * True when an interval-less user placed a paid order since the sale cutoff
   * — they already purchased, so no sale banner.
   */
  hasRecentPaidOrder: boolean;
}

const FALLBACK: SubscriptionSaleStatus = {
  interval: null,
  hasLifetime: false,
  hasRecentPaidOrder: false,
};

/**
 * Billing interval of the signed-in user's current Open Studio subscription,
 * plus whether they placed a paid order since the Summer Sale cutoff. Returns
 * the fallback (no interval, no recent order) on any error, matching the
 * pre-existing err-toward-general-banner behavior.
 */
export async function getSubscriptionInterval(): Promise<SubscriptionSaleStatus> {
  try {
    const { data, error } = await supabase.functions.invoke<SubscriptionSaleStatus>(
      'get-subscription-interval',
      { method: 'POST' },
    );

    if (error) {
      logger.error('get-subscription-interval error', {
        errorType: error.name || error.constructor.name,
        message: error.message || 'No message',
        isNetworkError: error instanceof TypeError,
      });
      return FALLBACK;
    }

    return {
      interval: data?.interval === 'month' || data?.interval === 'year' ? data.interval : null,
      hasLifetime: data?.hasLifetime === true,
      hasRecentPaidOrder: data?.hasRecentPaidOrder === true,
    };
  } catch (err) {
    logger.error('get-subscription-interval exception', { err });
    return FALLBACK;
  }
}
